/**
 * DataSyncContext - Provides real-time data synchronization across all pages
 * 
 * This context ensures that when data is updated on one page, all other pages
 * are automatically notified and can refresh their data.
 * 
 * Integrates with React Query for cache invalidation.
 */
import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export type DataType = 
  | 'tickets'
  | 'technicians'
  | 'customers'
  | 'calendar'
  | 'map'
  | 'dashboard'
  | 'inventory';

// Query keys mapping for React Query integration
const QUERY_KEYS: Record<DataType, string[]> = {
  tickets: ['tickets', 'ticket', 'kanban'],
  technicians: ['technicians', 'users'],
  customers: ['customers'],
  calendar: ['calendar', 'appointments', 'schedule'],
  map: ['map', 'locations'],
  dashboard: ['dashboard', 'stats'],
  inventory: ['inventory', 'parts'],
};

interface DataSyncContextType {
  // Trigger a refresh for specific data types
  triggerRefresh: (dataType: DataType | DataType[]) => void;
  
  // Subscribe to refresh events
  subscribeToRefresh: (dataType: DataType, callback: () => void) => () => void;
  
  // Get the last refresh timestamp for a data type
  getLastRefresh: (dataType: DataType) => number;
  
  // Force refresh all data
  refreshAll: () => void;
  
  // Notify that data has been updated (for optimistic updates)
  notifyDataUpdate: (dataType: DataType, data?: any) => void;
  
  // Get update count for a data type (useful for triggering re-renders)
  getUpdateCount: (dataType: DataType) => number;
}

const DataSyncContext = createContext<DataSyncContextType | undefined>(undefined);

export function DataSyncProvider({ children }: { children: React.ReactNode }) {
  const queryClient = useQueryClient();
  
  // Track last refresh time for each data type
  const [refreshTimestamps, setRefreshTimestamps] = useState<Record<DataType, number>>({
    tickets: Date.now(),
    technicians: Date.now(),
    customers: Date.now(),
    calendar: Date.now(),
    map: Date.now(),
    dashboard: Date.now(),
    inventory: Date.now(),
  });
  
  // Track update counts for each data type
  const [updateCounts, setUpdateCounts] = useState<Record<DataType, number>>({
    tickets: 0,
    technicians: 0,
    customers: 0,
    calendar: 0,
    map: 0,
    dashboard: 0,
    inventory: 0,
  });
  
  // Store callbacks for each data type
  const callbacksRef = useRef<Map<DataType, Set<() => void>>>(new Map());
  
  // Initialize callback sets
  useEffect(() => {
    const dataTypes: DataType[] = ['tickets', 'technicians', 'customers', 'calendar', 'map', 'dashboard', 'inventory'];
    dataTypes.forEach(type => {
      if (!callbacksRef.current.has(type)) {
        callbacksRef.current.set(type, new Set());
      }
    });
  }, []);
  
  // Trigger refresh for specific data types
  const triggerRefresh = useCallback((dataType: DataType | DataType[]) => {
    const types = Array.isArray(dataType) ? dataType : [dataType];
    const now = Date.now();
    
    // Update timestamps
    setRefreshTimestamps(prev => {
      const updated = { ...prev };
      types.forEach(type => {
        updated[type] = now;
      });
      return updated;
    });
    
    // Update counts
    setUpdateCounts(prev => {
      const updated = { ...prev };
      types.forEach(type => {
        updated[type] = (prev[type] || 0) + 1;
      });
      return updated;
    });
    
    // Invalidate React Query caches
    types.forEach(type => {
      const queryKeys = QUERY_KEYS[type] || [];
      queryKeys.forEach(key => {
        queryClient.invalidateQueries({ queryKey: [key] });
      });
    });
    
    // Notify all subscribers
    types.forEach(type => {
      const callbacks = callbacksRef.current.get(type);
      if (callbacks) {
        callbacks.forEach(callback => {
          try {
            callback();
          } catch (error) {
            console.error(`[DataSync] Error in refresh callback for ${type}:`, error);
          }
        });
      }
    });
    
    console.log(`[DataSync] Triggered refresh for: ${types.join(', ')}`);
  }, [queryClient]);
  
  // Subscribe to refresh events
  const subscribeToRefresh = useCallback((dataType: DataType, callback: () => void) => {
    if (!callbacksRef.current.has(dataType)) {
      callbacksRef.current.set(dataType, new Set());
    }
    
    callbacksRef.current.get(dataType)!.add(callback);
    
    // Return unsubscribe function
    return () => {
      callbacksRef.current.get(dataType)?.delete(callback);
    };
  }, []);
  
  // Get last refresh timestamp
  const getLastRefresh = useCallback((dataType: DataType) => {
    return refreshTimestamps[dataType] || 0;
  }, [refreshTimestamps]);
  
  // Get update count
  const getUpdateCount = useCallback((dataType: DataType) => {
    return updateCounts[dataType] || 0;
  }, [updateCounts]);
  
  // Refresh all data types
  const refreshAll = useCallback(() => {
    const allTypes: DataType[] = ['tickets', 'technicians', 'customers', 'calendar', 'map', 'dashboard', 'inventory'];
    triggerRefresh(allTypes);
  }, [triggerRefresh]);
  
  // Notify that data has been updated
  const notifyDataUpdate = useCallback((dataType: DataType, _data?: any) => {
    // Determine related data types that should also be refreshed
    const relatedTypes: Record<DataType, DataType[]> = {
      tickets: ['dashboard', 'calendar', 'map'],
      technicians: ['dashboard', 'calendar', 'map'],
      customers: ['tickets', 'dashboard'],
      calendar: ['dashboard', 'map'],
      map: ['dashboard'],
      dashboard: [],
      inventory: ['dashboard'],
    };
    
    const typesToRefresh = [dataType, ...(relatedTypes[dataType] || [])];
    triggerRefresh(typesToRefresh);
  }, [triggerRefresh]);
  
  const value: DataSyncContextType = {
    triggerRefresh,
    subscribeToRefresh,
    getLastRefresh,
    refreshAll,
    notifyDataUpdate,
    getUpdateCount,
  };
  
  return (
    <DataSyncContext.Provider value={value}>
      {children}
    </DataSyncContext.Provider>
  );
}

export function useDataSync() {
  const context = useContext(DataSyncContext);
  if (!context) {
    throw new Error('useDataSync must be used within a DataSyncProvider');
  }
  return context;
}

// Hook for automatic refresh subscription
export function useDataRefresh(dataType: DataType | DataType[], onRefresh: () => void) {
  const { subscribeToRefresh } = useDataSync();
  
  useEffect(() => {
    const types = Array.isArray(dataType) ? dataType : [dataType];
    const unsubscribes = types.map(type => subscribeToRefresh(type, onRefresh));
    
    return () => {
      unsubscribes.forEach(unsub => unsub());
    };
  }, [dataType, onRefresh, subscribeToRefresh]);
}

// Hook for getting update trigger (useful for useEffect dependencies)
export function useDataUpdateTrigger(dataType: DataType): number {
  const { getUpdateCount } = useDataSync();
  return getUpdateCount(dataType);
}
